class InfiniteProductGrid extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.onIntersect = this.onIntersect.bind(this);
  }

  connectedCallback() {
    this.mutationObserver = new MutationObserver(() => this.init());
    this.init();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  // Idempotent: safe to call again whenever the grid content is swapped
  // out from under us (e.g. by facets.js re-rendering #ProductGridContainer).
  init() {
    this.mutationObserver?.disconnect();

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    const grid = this.querySelector('ul.product-grid');
    const pagination = grid
      ? this.querySelector('.infinite-scroll-pagination')
      : null;

    this.grid = grid;
    this.pagination = pagination;
    this.nextUrl = pagination ? pagination.dataset.nextUrl || null : null;
    this.loading = false;

    if (grid && pagination && this.nextUrl) {
      this.loader = this.ensureLoader();
      this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
        rootMargin: '200px',
      });
      this.intersectionObserver.observe(pagination);
    }

    if (grid) {
      this.dispatchEvent(
        new CustomEvent('infinite-product-grid:init', { bubbles: true }),
      );
    }

    this.mutationObserver?.observe(this, { childList: true });
  }

  ensureLoader() {
    let loader = this.querySelector('.infinite-scroll-loader');
    if (loader) return loader;

    loader = document.createElement('div');
    loader.className = 'infinite-scroll-loader';
    loader.innerHTML = `
      <div class="dots-loader">
        <span></span><span></span><span></span>
      </div>
      <span class="visually-hidden">${this.dataset.loadingText || ''}</span>
    `;
    this.pagination.insertAdjacentElement('beforebegin', loader);
    return loader;
  }

  onIntersect(entries) {
    if (entries[0]?.isIntersecting) this.loadMore();
  }

  async loadMore() {
    if (!this.nextUrl || this.loading) return;

    this.loading = true;
    this.loader?.classList.add('is-visible');

    try {
      const response = await fetch(this.nextUrl);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newItems = doc.querySelectorAll('ul.product-grid > li');
      const newPagination = doc.querySelector('.infinite-scroll-pagination');

      newItems.forEach((item, index) => {
        item.classList.add('is-new');
        this.grid.appendChild(item);
        requestAnimationFrame(() => {
          setTimeout(() => item.classList.add('is-visible'), index * 60);
        });
      });

      this.nextUrl = newPagination?.dataset.nextUrl || null;

      if (!this.nextUrl && this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    } catch (error) {
      console.error(
        'infinite-product-grid: failed to load more products',
        error,
      );
    } finally {
      this.loading = false;
      this.loader?.classList.remove('is-visible');
    }
  }
}

customElements.define('infinite-product-grid', InfiniteProductGrid);
